/**
 * feat-248 — every on-screen string the role-remap surface renders, in one
 * module.
 *
 * Copy lives here rather than inline for the reason the repo has learned the
 * hard way: a literal-text grep returns a TRUE ZERO for text that is visibly on
 * screen when the copy renders through a constant. Keeping the whole vocabulary
 * in one file means the census is the file, not a guess about which literals
 * were spelled the same way.
 *
 * Strings are VERBATIM from `design/role-remap-NOTES.md` §2, curly quotes and
 * em-dashes included — the importer already uses them
 * (`PasteImportWizard.tsx:567`, `InferredItemCard.tsx:410`).
 *
 * SINGULAR AND PLURAL ARE BOTH REQUIRED. A hard-coded plural reads as a bug on
 * the one-item case, which is why every counted string here is a function of N
 * rather than a template with an `s` glued on.
 */

/** A pasted role can be any length. Truncate before it reaches a control:
 *  an `<option>` label cannot be truncated with CSS, so it has to happen in the
 *  string. The banner LEDE is the one place the value appears in full — it has
 *  `break-words` and the room for it (§5.5). */
export const ROLE_TRUNCATE_AT = 48;

export function truncateRole(raw: string): string {
  const v = raw.trim();
  return v.length > ROLE_TRUNCATE_AT ? `${v.slice(0, ROLE_TRUNCATE_AT)}…` : v;
}

// ---- Bulk remap banner (tenant HAS ≥1 prompt-backed role) ----

export const bulkBannerLede = (pasted: string): string =>
  `“${pasted}” isn’t a role in this tenant.`;

export const bulkBannerBody = (n: number): string =>
  `${n} rehearsal${n === 1 ? '' : 's'} use${n === 1 ? 's' : ''} it. They can’t be committed ` +
  `until each one points at a role that has an active prompt template.`;

export const BULK_SELECT_LABEL = 'Use this role instead';
export const ROLE_SELECT_PLACEHOLDER = '— pick a role —';

export const bulkApplyLabel = (n: number): string => `Apply to all ${n}`;

// ---- Zero prompt-backed roles for the tenant ----

export const ZERO_ROLES_LEDE = 'This tenant has no active prompt template versions.';

/** Rendered in three parts because `Prompt Management` is `font-bold` inline and
 *  deliberately NOT a link — the importer is a modal over the editor and there
 *  is no safe navigation out of it mid-paste. */
export const zeroRolesBodyBefore = (n: number): string =>
  `A rehearsal pins a prompt version when it saves, so none of the ${n} ` +
  `rehearsal${n === 1 ? '' : 's'} in this paste can be committed. Set up an active version in `;

export const ZERO_ROLES_BODY_EMPHASIS = 'Prompt Management';

export const zeroRolesBodyAfter = (n: number): string =>
  ` for at least one role, then import again — or drop the ${n} ` +
  `rehearsal${n === 1 ? '' : 's'} and commit the questions now.`;

/** Variant-4 `<option>` label. VERBATIM from the manual rehearsal editor
 *  (`QuizContentManagement.tsx:1338`). Do not improve it — two surfaces saying
 *  the same thing differently is how a product grows dialects. */
export const NO_ACTIVE_PROMPT_VERSIONS_OPTION = '— no active prompt versions —';

// ---- Roles fetch failed (degrade open) ----

export const DEGRADED_LEDE = 'Couldn’t load this tenant’s roles.';

/** Split around the inline `Role:` <code>, which gets the same treatment the
 *  importer's template help already gives Notion keywords. */
export const DEGRADED_BODY_BEFORE = 'Role checking is off for this import — the ';
export const DEGRADED_BODY_CODE = 'Role:';
export const DEGRADED_BODY_AFTER =
  ' line is sent exactly as you typed it, the same as before. If a role isn’t ' +
  'configured for this tenant, that rehearsal will fail at the commit step.';
export const DEGRADED_ACTION = 'Try again';

// ---- Per-item flag messages ----

export const unresolvedRoleMessage = (pasted: string): string =>
  `“${truncateRole(pasted)}” isn’t a role in this tenant — pick one that has an active prompt template.`;

export const ZERO_ROLES_ITEM_MESSAGE =
  'This tenant has no role with an active prompt template — see the note above.';

/** Variant-2 placeholder — the disabled, selected option that shows the user
 *  what they actually pasted, so they can connect the banner to this card. */
export const unresolvedRolePlaceholder = (pasted: string): string =>
  `— “${truncateRole(pasted)}” isn’t a role here —`;

// ---- Summary bar ----

/** Replaces the "Resolve all N …" helper ONLY in the zero-roles case. Short on
 *  purpose: the banner carries the remedy, the helper states the gate.
 *  Duplicating the remedy here makes the right-hand group wide enough to wrap
 *  and skews the summary row. */
export const zeroRolesSummaryHelper = (n: number): string =>
  `${n} rehearsal${n === 1 ? '' : 's'} block${n === 1 ? 's' : ''} commit — see the note above.`;

/** Neutral chip shown while the roles fetch is in flight, so the disabled
 *  Commit button is explained rather than mysterious (§5.6). */
export const CHECKING_ROLES_CHIP = 'Checking roles…';

/** `aria-label` on the role select. The wrapping `<label>` also contains the
 *  `*` marker span, so the accessible name would otherwise be `Role *`. */
export const ROLE_SELECT_ARIA_LABEL = 'Role';
